<template>
  <v-card>
    <!-- Dialogue de suppression -->
    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="headline">Confirm Deletion</v-card-title>
        <v-card-text>
          Are you sure you want to delete {{ selectedVideos.length }} selected video(s)? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn 
            color="red darken-1" 
            text 
            @click="deleteVideo" 
            :loading="deleting" 
          >
            <font-awesome-icon icon="fa-solid fa-trash" class="mr-2"/>
            Delete ({{ selectedVideos.length }})
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogue de description -->
    <v-dialog v-model="descriptionDialog" max-width="600">
      <v-card>
        <v-card-title class="headline">
          <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-2"/>
          {{ selectedVideo.resource_name }}
        </v-card-title>
        <v-card-text>
          <video 
            v-if="selectedVideo.get_file"
            :src="selectedVideo.get_file"
            controls
            class="mb-4"
            style="width: 100%; max-height: 300px;"
          ></video>
          <div class="text--primary pre-wrap">
            {{ selectedVideo.resource_description || 'No description available' }}
          </div>
          <v-divider class="my-3"></v-divider>
          <div class="text-caption">
            <div>Uploaded by: {{ selectedVideo.uploader }}</div>
            <div>Date: {{ selectedVideo.get_date_str }}</div>
            <div>Duration: {{ selectedVideo.duration }}</div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="descriptionDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- En-tête -->
    <v-card-title class="px-4">
      <span class="me-3">({{ resourcesList.length }}) Advert Videos</span>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        label="Search videos..."
        single-line
        hide-details
        class="mx-4"
      ></v-text-field>
      <div>
        <v-btn 
        color="error"
        class="text-capitalize mr-2"
        @click="openDeleteDialog"
        :disabled="selectedVideos.length === 0"
        depressed
      >
        <font-awesome-icon icon="fa-solid fa-trash" class="mr-2"/>
        Delete Selected
      </v-btn>
        <v-btn
          color="primary"
          class="text-capitalize"
          to="/savings/Business/AddBusinessVideos"
        >
          <font-awesome-icon icon="fa-solid fa-plus" class="mr-2"/>
          Add New Video
        </v-btn>
      </div>
    </v-card-title>

    <!-- Tableau des vidéos -->
    <v-data-table
      :headers="headers"
      :items="filteredVideos"
      :search="search"
      item-key="id"
      class="elevation-1 px-4"
      :items-per-page="10"
      mobile-breakpoint="0"
      show-select
      v-model="selectedVideos"
    >
      <!-- Colonne de sélection -->
      <template #[`item.data-table-select`]="{ isSelected, select }">
        <v-simple-checkbox
          :value="isSelected"
          @input="select($event)"
          color="primary"
        ></v-simple-checkbox>
      </template>

      <!-- Nom et description -->
      <template #[`item.resource_name`]="{ item }">
        <div class="d-flex flex-column py-2">
          <span class="font-weight-medium text-truncate">
            <font-awesome-icon icon="fa-solid fa-video" class="mr-2"/>
            {{ item.resource_name }}
          </span>
          <small 
            class="text-caption text-primary cursor-pointer"
            @click.stop="openDescriptionDialog(item)"
          >
            <font-awesome-icon icon="fa-solid fa-info-circle" class="mr-1"/>
            See full description
          </small>
        </div>
      </template>

      <!-- Description -->
      <template #[`item.resource_description`]="{ item }">
        <div class="text-truncate" style="max-width: 300px;">
          {{ item.resource_description || 'No description available' }}
        </div>
      </template>

      <!-- Téléchargement -->
      <template #[`item.get_file`]="{ item }">
        <v-tooltip top>
          <template #activator="{ on }">
            <v-btn 
              icon
              color="primary"
              @click="downloadVideo(item)"
              v-on="on"
              :loading="item.id === downloadingId"
            >
              <font-awesome-icon icon="fa-solid fa-download"/>
            </v-btn>
          </template>
          <span>Download video ({{ item.resource_size }})</span>
        </v-tooltip>
      </template>

      <!-- Actions -->
      <template #[`item.actions`]="{ item }">
        <v-btn 
          icon
          color="info"
          @click.stop="openDescriptionDialog(item)"
        >
          <font-awesome-icon icon="fa-solid fa-info-circle"/>
        </v-btn>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import axios from 'axios'

export default {
  data: () => ({
    headers: [
      { text: 'Select', value: 'data-table-select', sortable: false },
      { text: 'Video Title', value: 'resource_name' },
      { text: 'Description', value: 'resource_description' },
      { text: 'Size', value: 'resource_size', align: 'center' },
      { text: 'Uploader', value: 'uploader' },
      { text: 'Date', value: 'get_date_str' },
      { text: 'Download', value: 'get_file', align: 'center', sortable: false },
      { text: 'Actions', value: 'actions', align: 'center', sortable: false }
    ],
    resourcesList: [],
    search: '',
    deleteDialog: false,
    descriptionDialog: false,
    selectedVideos: [],
    selectedVideo: {},
    deleting: false,
    downloadingId: null
  }),

  computed: {
    filteredVideos() {
      return this.resourcesList.filter(video => 
        video.resource_name.toLowerCase().includes(this.search.toLowerCase()) ||
        (video.resource_description && video.resource_description.toLowerCase().includes(this.search.toLowerCase())))
    }
  },

  mounted() {
    this.getAdResources()
    document.title = "Manager: Savings | List of Ad Resources"
  },

  methods: {
    getAdResources() {
      axios.get('/api/v1/manager/bussinesget/ad_resources/Video/Savings/')
        .then(response => {
          console.log("DEBUG - Données reçues :", response.data)
          this.resourcesList = response.data
        })
        .catch(error => {
          this.handleError(error)
        })
    },



    openDescriptionDialog(video) {
      this.selectedVideo = { ...video }
      this.descriptionDialog = true
    },

    openDeleteDialog() {
      if (this.selectedVideos.length > 0) {
        this.deleteDialog = true
      }
    },
// Modification de la méthode deleteVideo
async deleteVideo() {
  this.deleting = true;
  try {
    const ids = this.selectedVideos.map(v => v.id);
    
    const response = await axios.post(
      '/api/v1/manager/delete_videos/',
      { ids },
      {

      }
    );

    // Filtrage côté client pour meilleure performance
    this.resourcesList = this.resourcesList.filter(
      video => !ids.includes(video.id)
    );
    
    this.showSuccess(response.data.detail);
    setTimeout(() => {
      window.location.reload();
    }, 500);
  } catch (error) {
    const errorMsg = error.response?.data?.detail || "Erreur inconnue";
    this.showError(errorMsg);
  } finally {
    this.deleting = false;
    this.deleteDialog = false;
    
  }
},
    async downloadVideo(item) {
      this.downloadingId = item.id
      try {
        const response = await axios.get(item.get_file, {
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${this.$store.state.accessToken}`
          }
        })
        
        const blob = new Blob([response.data])
        const link = document.createElement('a')
        link.href = URL.createObjectURL(blob)
        link.download = item.resource_name + this.getFileExtension(item.get_file)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        URL.revokeObjectURL(link.href)
      } catch (error) {
        this.showError("Failed to download video")
      } finally {
        this.downloadingId = null
      }
    },

    getFileExtension(url) {
      const match = url.match(/\.([a-zA-Z0-9]+)(?:[\?#]|$)/)
      return match ? match[0] : '.mp4'
    },

    handleError(error, defaultMessage = "An error occurred") {
      const message = error.response?.data?.detail || defaultMessage
      this.showError(message)
    },

    showSuccess(message) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', "success")
      this.$store.commit('activateSnackBar', true)
    },

    showError(message) {
      this.$store.commit('setSnackBarMessage', message)
      this.$store.commit('setSnackBarColor', "error")
      this.$store.commit('activateSnackBar', true)
    }
  }
}
</script>

<style scoped>
.pre-wrap {
  white-space: pre-wrap;
}

.cursor-pointer {
  cursor: pointer;
}

.v-data-table >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.v-card-title {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}

.font-awesome-icon {
  font-size: 1.1em;
  vertical-align: middle;
}

.text-truncate {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>